import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, List, Empty, Spin, message } from 'antd';
import { PictureOutlined, BookOutlined, RightOutlined } from '@ant-design/icons';
import { albumAPI, blogAPI, userAPI } from '../utils/api';
import { IMG_URL } from '../utils/api';
import useDocumentTitle from '../hooks/useDocumentTitle';

const Index = () => {
  const { username: routeUsername } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    userCount: 0,
    albumCount: 0,
    blogCount: 0
  });
  const [latestAlbums, setLatestAlbums] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);

  // 根据是否查看特定用户的首页动态设置网页标题
  const pageTitle = routeUsername ? `${routeUsername} 的首页` : 'rtycn - 首页';
  useDocumentTitle(pageTitle);

  useEffect(() => {
    fetchStats();
    fetchLatestAlbums();
    fetchLatestBlogs();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, albumsRes, blogsRes] = await Promise.all([
        userAPI.getAllUsers(),
        routeUsername ? albumAPI.getAlbumsByAuthor(routeUsername) : Promise.resolve({ data: [] }),
        routeUsername ? blogAPI.getBlogsByAuthor(routeUsername) : Promise.resolve({ data: [] })
      ]);

      // 验证用户是否存在
      if (routeUsername && usersRes.data) {
        const userExists = usersRes.data.some(u => u.username === routeUsername);
        if (!userExists) {
          navigate(`/${routeUsername}/error`, {
            state: {
              errorInfo: {
                title: '用户不存在',
                description: `用户 "${routeUsername}" 不存在`
              }
            },
            replace: true
          });
          return;
        }
      }

      setStats({
        userCount: usersRes.data?.length || 0,
        albumCount: albumsRes.data?.length || 0,
        blogCount: blogsRes.data?.length || 0
      });
    } catch (error) {
      console.error('获取统计数据失败:', error);
      navigate(`/${routeUsername}/error`, {
        state: {
          errorInfo: {
            title: '数据加载失败',
            description: '无法获取统计数据，请稍后重试'
          }
        },
        replace: true
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestAlbums = async () => {
    try {
      // 获取路由用户的最新相册
      if (routeUsername) {
        const response = await albumAPI.getLatestAlbumsByAuthor(routeUsername, 5);
        setLatestAlbums(response.data || []);
      } else {
        setLatestAlbums([]);
      }
    } catch (error) {
      console.error('获取最新相册失败:', error);
      setLatestAlbums([]);
    }
  };

  const fetchLatestBlogs = async () => {
    try {
      // 获取路由用户的最新博客
      if (routeUsername) {
        const response = await blogAPI.getLatestBlogsByAuthor(routeUsername, 5);
        setLatestBlogs(response.data || []);
      } else {
        setLatestBlogs([]);
      }
    } catch (error) {
      console.error('获取最新博客失败:', error);
      setLatestBlogs([]);
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h3>欢迎来到{pageTitle}</h3>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="相册总数"
              value={stats.albumCount}
              prefix={<PictureOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="博客总数"
              value={stats.blogCount}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card title="最新相册">
            {latestAlbums.length === 0 ? (
              <Empty description="暂无相册" />
            ) : (
              <List
                dataSource={latestAlbums}
                renderItem={(album) => (
                  <List.Item
                    actions={[
                      <a onClick={() => window.location.href = `/${album.authorId}/album/?id=${album.id}`}>
                        查看 <RightOutlined />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <img
                          src={album.coverImage ? `${IMG_URL}${album.coverImage}` : '/img/default-album.png'}
                          alt={album.title}
                          style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                        />
                      }
                      title={album.title}
                      description={`${album.authorName || album.authorId} · ${album.photoCount || 0} 张照片`}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="最新博客">
            {latestBlogs.length === 0 ? (
              <Empty description="暂无博客" />
            ) : (
              <List
                dataSource={latestBlogs}
                renderItem={(blog) => (
                  <List.Item
                    actions={[
                      <a onClick={() => window.location.href = `/${blog.authorId}/blog/article/?id=${blog.id}`}>
                        阅读 <RightOutlined />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      title={blog.title}
                      description={
                        <div>
                          <div>{blog.summary}</div>
                          <div style={{ color: '#999', fontSize: '12px', marginTop: 4 }}>
                            {blog.authorName || blog.authorId} · {blog.viewCount || 0} 次浏览
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Index;